// src/pages/AccountSecurityPage.js
//
// "Sign-in methods" page — web parity with the iOS AccountScreen
// signInMethodsSection. Lists every credential currently linked to the
// signed-in Firebase user and offers Link controls for unlinked OAuth
// providers + a Re-verify Phone control for users without a phone credential.
//
// Linking happens against `auth.currentUser` via `linkProviderToCurrentUser`
// (linkWithPopup under the hood), so the same Firebase UID accumulates
// credentials and the user can subsequently sign in via any of them.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { EmailAuthProvider, linkWithCredential } from "firebase/auth";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { auth } from "../firebase";
import { useAuth } from "../components/shared/AuthContext";
import {
  linkProviderToCurrentUser,
  startPhoneVerification,
  linkPhoneToCurrentUser,
  resetRecaptcha,
  toE164,
} from "../services/auth.providers";

const PROVIDERS = [
  { id: "google.com", label: "Google" },
  { id: "apple.com", label: "Apple" },
  { id: "phone", label: "Phone" },
  { id: "password", label: "Email & Password" },
];

export default function AccountSecurityPage() {
  const navigate = useNavigate();
  const { user, userInfo, refreshUserInfo } = useAuth();

  // Re-read providerData after each successful link via a tick state — the
  // Firebase user object on the AuthContext doesn't deep-update its array.
  const [tick, setTick] = useState(0);
  const linked = useMemo(() => {
    void tick;
    return new Set((user?.providerData || []).map((p) => p.providerId));
  }, [user, tick]);

  const [busyProvider, setBusyProvider] = useState(null);
  const [error, setError] = useState("");
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const handleLinkProvider = async (providerId) => {
    setError("");
    setBusyProvider(providerId);
    try {
      await linkProviderToCurrentUser(providerId);
      await auth.currentUser?.getIdToken(true);
      setTick((t) => t + 1);
      try { await refreshUserInfo(); } catch {}
    } catch (err) {
      setError(translate(err) || `Could not link ${providerId}.`);
    } finally {
      setBusyProvider(null);
    }
  };

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <button type="button" onClick={() => navigate(-1)} style={backBtnStyle} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <h1 style={titleStyle}>Sign-in methods</h1>
      </header>

      <p style={subStyle}>
        Add more ways to sign in. All linked methods resolve to the same Polyp account.
      </p>

      <div style={listStyle}>
        {PROVIDERS.map((p) => (
          <ProviderRow
            key={p.id}
            label={p.label}
            linked={linked.has(p.id)}
            busy={busyProvider === p.id}
            phoneNumberOnFile={userInfo?.phoneNumber}
            providerId={p.id}
            onLink={() => {
              if (p.id === "phone") {
                setPhoneModalOpen(true);
              } else if (p.id === "password") {
                setPasswordModalOpen(true);
              } else {
                handleLinkProvider(p.id);
              }
            }}
          />
        ))}
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {phoneModalOpen && (
        <PhoneLinkModal
          phoneNumber={userInfo?.phoneNumber}
          onClose={() => setPhoneModalOpen(false)}
          onLinked={async () => {
            try { await auth.currentUser?.getIdToken(true); } catch {}
            try { await refreshUserInfo(); } catch {}
            setTick((t) => t + 1);
            setPhoneModalOpen(false);
          }}
        />
      )}

      {passwordModalOpen && (
        <SetPasswordModal
          defaultEmail={userInfo?.email || auth.currentUser?.email || ""}
          onClose={() => setPasswordModalOpen(false)}
          onLinked={async () => {
            try { await auth.currentUser?.getIdToken(true); } catch {}
            try { await refreshUserInfo(); } catch {}
            setTick((t) => t + 1);
            setPasswordModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

function SetPasswordModal({ defaultEmail, onClose, onLinked }) {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const checks = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /\d/.test(password),
  };
  const passwordValid = Object.values(checks).every(Boolean);
  const passwordsMatch = password === confirm && password.length > 0;

  const submit = async () => {
    setError("");
    if (!email.trim()) { setError("Email is required."); return; }
    if (!passwordValid) { setError("Password must be 8+ chars with uppercase, lowercase, and a number."); return; }
    if (!passwordsMatch) { setError("Passwords don't match."); return; }
    if (!auth.currentUser) { setError("Not signed in."); return; }
    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(email.trim(), password);
      await linkWithCredential(auth.currentUser, credential);
      onLinked();
    } catch (err) {
      const code = err?.code || "";
      if (code === "auth/email-already-in-use") {
        setError("This email is already used by another Polyp account.");
      } else if (code === "auth/provider-already-linked") {
        setError("Email/password is already linked to this account.");
      } else if (code === "auth/weak-password") {
        setError("Password is too weak. Use 8+ chars with mixed case and a number.");
      } else if (code === "auth/requires-recent-login") {
        setError("Please sign out and sign back in, then try again.");
      } else {
        setError(err?.message || "Could not set password.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#fff" }}>Set a password</h2>
        <p style={{ marginTop: 8, marginBottom: 16, fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
          Adds email/password as a backup sign-in method on this account.
        </p>

        <input
          type="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={pwInputStyle}
        />
        <input
          type="password"
          autoComplete="new-password"
          placeholder="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={pwInputStyle}
        />
        {password && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 11, marginBottom: 8 }}>
            <span style={{ color: checks.length ? "#34d399" : "rgba(255,255,255,0.4)" }}>8+ chars</span>
            <span style={{ color: checks.upper ? "#34d399" : "rgba(255,255,255,0.4)" }}>Uppercase</span>
            <span style={{ color: checks.lower ? "#34d399" : "rgba(255,255,255,0.4)" }}>Lowercase</span>
            <span style={{ color: checks.number ? "#34d399" : "rgba(255,255,255,0.4)" }}>Number</span>
          </div>
        )}
        <input
          type="password"
          autoComplete="new-password"
          placeholder="Confirm password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          style={pwInputStyle}
        />

        <button
          type="button"
          style={primaryBtn}
          disabled={loading || !passwordValid || !passwordsMatch || !email.trim()}
          onClick={submit}
        >
          {loading ? "Linking…" : "Set password"}
        </button>

        {error && <div style={errorStyle}>{error}</div>}

        <button type="button" style={ghostBtn} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

const pwInputStyle = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  fontSize: 15,
  marginBottom: 10,
  boxSizing: "border-box",
};

function ProviderRow({ label, linked, busy, providerId, phoneNumberOnFile, onLink }) {
  const phoneNoNumber = providerId === "phone" && !phoneNumberOnFile;
  return (
    <div style={rowStyle}>
      <div style={{ flex: 1, color: "#fff", fontSize: 15 }}>{label}</div>
      {linked ? (
        <span style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>Linked</span>
      ) : phoneNoNumber ? (
        <span style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>No number on file</span>
      ) : busy ? (
        <span style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>Linking…</span>
      ) : (
        <button type="button" onClick={onLink} style={linkBtnStyle}>Link</button>
      )}
    </div>
  );
}

function PhoneLinkModal({ phoneNumber, onClose, onLinked }) {
  const [step, setStep] = useState("phone");
  const [confirmation, setConfirmation] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => () => resetRecaptcha(), []);

  const sendCode = async () => {
    if (!phoneNumber) { setError("No number on file."); return; }
    setError(""); setLoading(true);
    try {
      const conf = await startPhoneVerification(toE164(phoneNumber), "recaptcha-security-page");
      setConfirmation(conf);
      setStep("code");
    } catch (err) {
      setError(translate(err) || "Could not send code.");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!confirmation) { setError("Session expired. Resend code."); return; }
    setError(""); setLoading(true);
    try {
      await linkPhoneToCurrentUser(confirmation, code);
      onLinked();
    } catch (err) {
      setError(translate(err) || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#fff" }}>Verify your phone</h2>
        <p style={{ marginTop: 8, marginBottom: 16, fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
          We'll send a 6-digit code to <strong style={{ color: "#fff" }}>{formatPhone(phoneNumber)}</strong>.
        </p>

        <div id="recaptcha-security-page" />

        {step === "phone" && (
          <button type="button" style={primaryBtn} disabled={loading} onClick={sendCode}>
            {loading ? "Sending…" : "Send code"}
          </button>
        )}

        {step === "code" && (
          <>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              style={codeInputStyle}
              autoFocus
            />
            <button type="button" style={primaryBtn} disabled={loading || code.length !== 6} onClick={verifyCode}>
              {loading ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              style={ghostBtn}
              disabled={loading}
              onClick={() => { setCode(""); setConfirmation(null); setStep("phone"); }}
            >
              Resend code
            </button>
          </>
        )}

        {error && <div style={errorStyle}>{error}</div>}

        <button type="button" style={ghostBtn} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function translate(err) {
  const code = err?.code || "";
  if (code === "auth/credential-already-in-use") return "This sign-in method is already linked to another Polyp account.";
  if (code === "auth/invalid-verification-code") return "Invalid code. Try again.";
  if (code === "auth/code-expired") return "Code expired. Request a new one.";
  if (code === "auth/popup-closed-by-user") return "Sign-in cancelled.";
  if (code === "auth/popup-blocked") return "Pop-up was blocked. Allow pop-ups and try again.";
  if (code === "auth/provider-already-linked") return "Already linked.";
  return err?.message || "";
}

function formatPhone(value) {
  if (!value) return value;
  const parsed = parsePhoneNumberFromString(String(value), "US");
  if (parsed?.isValid()) {
    return parsed.country === "US" || parsed.country === "CA"
      ? parsed.formatNational()
      : parsed.formatInternational();
  }
  return value;
}

// ─── styles ──────────────────────────────────────────────────────────────
const pageStyle = {
  minHeight: "100vh",
  background: "#0a0a0a",
  color: "#fff",
  padding: "20px 24px 40px",
  maxWidth: 640,
  margin: "0 auto",
};

const headerStyle = { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 };
const backBtnStyle = { background: "transparent", border: "none", color: "#fff", cursor: "pointer", padding: 4 };
const titleStyle = { margin: 0, fontSize: 24, fontWeight: 600 };
const subStyle = { fontSize: 14, color: "rgba(255,255,255,0.6)", marginBottom: 24 };

const listStyle = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 12,
  overflow: "hidden",
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  padding: "16px 18px",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
};

const linkBtnStyle = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.15)",
  background: "rgba(255,255,255,0.08)",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn = {
  padding: "10px 16px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.15)",
  background: "rgba(255,255,255,0.12)",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  marginBottom: 8,
  width: "100%",
};

const ghostBtn = {
  padding: "10px 16px",
  borderRadius: 10,
  border: "none",
  background: "transparent",
  color: "rgba(255,255,255,0.5)",
  fontSize: 13,
  cursor: "pointer",
  width: "100%",
};

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  background: "rgba(0,0,0,0.5)",
  display: "grid",
  placeItems: "center",
  padding: 20,
};

const modalStyle = {
  background: "rgba(0,0,0,0.95)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 16,
  padding: 24,
  width: "100%",
  maxWidth: 380,
  color: "#fff",
};

const codeInputStyle = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  fontSize: 22,
  letterSpacing: 8,
  textAlign: "center",
  marginBottom: 12,
};

const errorStyle = {
  marginTop: 16,
  padding: "10px 14px",
  borderRadius: 10,
  background: "rgba(239,68,68,0.15)",
  border: "1px solid rgba(239,68,68,0.2)",
  color: "#f87171",
  fontSize: 13,
};
